<template>
	<Card :no-header="true">
		<template #content>
			<div class='disconnect-tip' v-show="displayReconnectTip === 1">
				目前離線中，重新嘗試連線！
			</div>
			<div class='reconnected-tip' v-show="displayReconnectTip === 2">
				已重新連線！
			</div>
			<div :class="chatClass" data-style="telegram" ref="chat">
				<div
					v-for="(message, index) in messages"
					:class="messageClass(message)"
					:key="index"
				>
					<div
						class="avatar"
						:data-initial="!toClientAvatar ? toClientName : false"
					>
						<img v-if="toClientAvatar" :src="toClientAvatar" />
					</div>
					<div class="content">
						<div class="author">{{ toClientName }}</div>
						<!-- 文字 -->
						<div class="text" v-if="message.contentType === 'text'">
							<p>{{ message.content }}</p>
						</div>
						<img
							:src="message.content"
							v-else-if="message.contentType === 'image'"
						/>
						<!-- 中繼資料 -->
						<div class="meta">
							<div class="item">
								{{ message.createdAt | time }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer>
			<div class="operators">
				<!-- input message textarea -->
				<textarea
					class="input-textarea"
					cols="20"
					v-model.trim="inputMessage"
					ref="inputMessage"
					:placeholder="$t('chatroom.input_message')"
				></textarea>
				<!-- upload image button -->
				<button class="common-btn default">
					<label for="file-input">
						<span class="iconfont icon-jia1"></span>
					</label>

					<input
						type="file"
						id="file-input"
						accept="image/*"
						style="display: none"
						@change="uploadImage"
					/>
				</button>
				<!-- send message button -->
				<mt-button
					class="common-btn default"
					@click="sendMessage"
					:disabled="submitButtonDisabled || inputMessage.length === 0"
					>{{ $t('chatroom.send') }}</mt-button
				>
			</div>

			<!-- preview upload image modal -->
			<mt-popup v-model="imageVisible">
				<img :src="imageEncode" class="preview-image" />
				<div class="upload-image-operators">
					<mt-button class="common-btn default" @click="clearImageEncode">
						{{ $t('general.cancel') }}
					</mt-button>

					<mt-button
						@click="sendImage"
						:disabled="submitButtonDisabled"
						:class="{
							'common-btn': true,
							default: true,
							'send-image-clicked': isClickedForSendImage
						}"
					>
						{{ $t('general.confirm') }}
					</mt-button>
				</div>
			</mt-popup>
		</template>
	</Card>
</template>

<script>
import { Toast } from 'mint-ui'
import Compressor from 'compressorjs'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment from 'moment'
import Card from '@/components/basic/card'
export default {
	name: 'Chatroom',
	filters: {
		time: function (val) {
			return moment(val).format('HH:mm')
		}
	},
	sockets: {
		sendMessage: function (payload) {
			if (this.$attrs.visible && payload.roomId === this.roomId) {
				this.readMessages({ roomId: this.roomId })
					.then(()=>{
						this.initialChatData({
							roomIds: [this.roomId]
						})
					})
					.then(() => {
						this.gotoLastmessage()
					})
					.catch((err) => {
						console.error('readed message error', err)
					})
			}
		}
	},
	components: {
		Card
	},
	props: {
		fromPopup: {
			type: Boolean,
			required: false,
			default: false
		},
		params: {
			role : {
				type : String,
				required: true
			},
			name : {
				type : String,
				required: true,
			},
			orderSn : {
				type: String,
				required: true
			},
			orderId: {
				type: Number,
				required: true
			},
			orderType: {
				type: String,
				required: true
			},
			toClientType: {
				type: String,
				required: true
			},
		},
		toClientName: {
			type: String,
			required: false,
			default: function () {
				return this.$t(`chatroom.client_name_alias_${this.params.toClientType}`)
			}
		},
		toClientAvatar: {
			type: String,
			required: false,
			default: function () {
				return undefined
			}
		}
	},
	data() {
		return {
			roomId: undefined,
			isClickedForSendImage: false,
			imageVisible: false,
			imageEncode: undefined,
			imageBuffer: undefined,
			inputMessage: '',
			submitButtonDisabled: false,
			displayReconnectTip: 0,
			timers: {
				readMessageTimer: undefined,
				reconnectTimer: undefined
			}
		}
	},
	computed: {
		...mapGetters(['getRoomMessagesByRoomId', 'getReconnectState']),
		...mapState({
			refreshCount: (state) => state.chatroom.refreshCount,
			isMemberAuthorized: (state) => state.member.token,
			isDistributorAuthorized: (state) => state.distributor.token,
			memberInfo: (state) => state.member.info,
			distributorInfo: (state) => state.distributor.info
		}),
		chatClass: function () {
			return {
				cu: true,
				chat: true,
				popup: this.fromPopup
			}
		},
		messages: function () {
			let messages = []
			if (this.roomId && this.refreshCount) {
				try {
					messages = this.getRoomMessagesByRoomId(this.roomId)
				} catch (err) {
					console.error(err)
				}
			}
			this.gotoLastmessage()
			return messages
		},
		clientId: function () {
			if (this.isMemberAuthorized) {
				return this.memberInfo.member_id
			} else if (this.isDistributorAuthorized) {
				return this.distributorInfo.user_id
			} else {
				return undefined
			}
		}
	},
	watch: {
		getReconnectState: function (val) {
			if (val) {
				this.displayReconnectTip = 1
			} else {
				this.displayReconnectTip = 2
				setTimeout(() => {
					this.displayReconnectTip = 0
				}, 3000)
			}
		},
		roomId: function (val) {
			if (!val) {
				this.$alert.error(this.$t('chatroom.error_no_room_id'))
			}
		},
		'$attrs.visible': {
			immediate: true,
			handler: function (val) {
				if (val && this.params.orderId !== 0) {
					this.initialRoomMessages()
				}
			}
		}
	},
	created() {
		if (this.getReconnectState) {
			this.displayReconnectTip = 1
		}
	},
	methods: {
		...mapActions(['initialChatData', 'joinRoom', 'readMessages']),
		initialRoomMessages() {
			this.joinRoom(this.params)
				.then((roomId) => {
					this.roomId = roomId
				})
				.then(async () => {
					await this.readMessages({
						roomId: this.roomId
					})
				})
				.then(async () => {
					await this.initialChatData({
						roomIds: [this.roomId]
					})
				})
				.then(() => {
					this.gotoLastmessage()
				})
				.catch((err) => {
					console.error(err)
				})
		},
		messageClass(message) {
			return {
				message: true,
				img: message.contentType === 'image',
				text: message.contentType === 'text',
				right: this.messageRight(message)
			}
		},
		messageRight(message) {
			return message.clientType !== this.params.toClientType
		},
		gotoLastmessage() {
			const _this = this
			this.$nextTick(() => {
				let messageDisplay = _this.$refs.chat
				messageDisplay.scrollTop = messageDisplay.scrollHeight
			})
		},
		clearImageEncode() {
			this.imageEncode = undefined
			this.imageVisible = false
		},
		sendImage() {
			this.submitButtonDisabled = true
			this.isClickedForSendImage = true
			const params = {
				role: this.params.role,
				name: this.params.name,
				orderSn: this.params.orderSn,
				roomId: this.roomId,
				clientId: this.clientId,
				contentType: 'image',
				content: this.imageEncode,
				toClientType: this.params.toClientType
			}
			this.$socket.emit('sendMessage', params, this.handleSendMessageCallback)
		},
		uploadImage(event) {
			const input = event.target

			if (input.files.length === 0) {
				return
			}

			const file = input.files[0]
			const reader = new FileReader()
			const _this = this

			new Compressor(file, {
				quality: 0.6,
				maxHeight: 1000,
				maxWidth: 1000,
				success(result) {
					if (parseFloat(result.size / 1024 / 1024).toPrecision(3) > 5) {
						this.$alert.error(
							this.$t('chatroom.error_image_size_too_large', { size: 5 })
						)
						input.value = ''
						return
					}

					reader.readAsDataURL(result)
					reader.onload = function (e) {
						const dataURL = e.target.result
						_this.imageEncode = dataURL
						_this.imageVisible = true

						let messageBoxZindex = undefined
						const observer = new MutationObserver((mutations) => {
							mutations.forEach((mutationRecord) => {
								const zindex = parseInt(
									mutationRecord.target.style.getPropertyValue('z-index')
								)
								if (messageBoxZindex === undefined) {
									messageBoxZindex = zindex
								}
								if (messageBoxZindex > zindex) {
									mutationRecord.target.remove()
								}
							})
						})
						try {
							observer.observe(document.querySelector('.v-modal'), {
								attributes: true,
								attributeFilter: ['style']
							})
						} catch (e) {
							if (e instanceof TypeError) {
							}
						}
					}
				},
				error(err) {
					Toast('錯誤: ' + err.message)
					console.error(err)
				}
			})
		},
		sendMessage() {
			try {
				this.submitButtonDisabled = true
				const params = {
					role: this.params.role,
					name: this.params.name,
					orderSn: this.params.orderSn,
					roomId: this.roomId,
					clientId: this.clientId,
					contentType: 'text',
					content: this.inputMessage,
					toClientType: this.params.toClientType
				}
				this.$socket.emit('sendMessage', params, this.handleSendMessageCallback)				
			} catch (e) {
				console.log(e)
			}
			
		},
		handleSendMessageCallback(response) {
			if (response.code === 0) {
				this.inputMessage = ''
				this.isClickedForSendImage = false
				this.submitButtonDisabled = false
				this.gotoLastmessage()
				this.clearImageEncode()
			} else {
				this.initialRoomMessages()
				this.$alert.error(this.$t('chatroom.error_send_message_failed'))
			}
		}
	}
}
</script>

<style src="@/assets/style/chat-ui/css/ChatUI.css"></style>
<style lang='scss' scoped>
.card-wrapper {
	padding: 0;
}
.chat {
	overflow-y: scroll;
	overflow-x: hidden;
}
@media only screen and (min-height: 0px) and (max-height: 568px) {
	.chat.popup {
		height: 55vh;
	}
	.chat {
		height: 53vh;
	}
}
@media only screen and (min-height: 569px) and (max-height: 811px) {
	.chat.popup {
		height: 65vh;
	}
	.chat {
		height: 60vh;
	}
}
@media only screen and (min-height: 812px) and (max-height: 1024px) {
	.chat.popup {
		height: 70vh;
	}
	.chat {
		height: 63vh;
	}
}
.author {
	color: $primaryColor;
}
.input-textarea {
	-webkit-border-radius: 5px;
	-moz-border-radius: 5px;
	border-radius: 5px;
	border: none;
	resize: none;
	margin-left: 0.3rem;
	margin-bottom: 0.3rem;
}
.disconnect-tip {
	color: $primaryColor;
	text-align: center;
	font-weight: bold;
}
.reconnected-tip {
	color: $green;
	text-align: center;
	font-weight: bold;
}
.preview-image {
	width: 90%;
	height: 90%;
}

.upload-image-operators {
	display: flex;
	justify-content: space-between;
	margin: 1rem;
}
</style>
